import {
  Button,
  Card,
  Col,
  Divider,
  Dropdown,
  Icons,
  List,
  Menu,
  Modal,
  notification,
  Popover,
  Row,
  Table,
  Typography,
  UploadFile,
} from '@pankod/refine-antd';
import { file2Base64, useNavigation } from '@pankod/refine-core';
import { UploadChangeParam } from 'antd/lib/upload';
import { DownloadCsvTemplateCard, ImportCsvDataCard } from 'components';
import { useRef, useState } from 'react';
import {
  importProductsForCreate,
  importProductsForUpdate,
  mapProductsAndImages,
  uploadProductMedia,
} from 'services/product';
import { isJpgOrPngFile, isLessThanSize, MAX_UPLOAD_FILE_SIZE_MB, readCSV } from 'utilities/file';
import {
  excelSafeImport,
  findDuplicate,
  getInvalidCharacterErrorMessage,
  NAME_REGEX,
  parseProductUniqueId,
  SKU_REGEX,
} from 'utilities/shared';
import { useFeatureFlag } from 'hooks/useFeatureFlags';
import {
  isValidImportPurchasePrice,
  optionalPurchasePriceFlag,
} from 'utilities/product-purchase-price';
import styles from './style.module.css';

export const CommonColumn = {
  ProductName: 'Product Name',
  ProductVariantSku: 'Variant Code/SKU',
  UnitOfMeasure: 'Unit of Measure',
  DefaultSalesPrice: 'Default Sales Price',
  DefaultPurchasePrice: 'Default Purchase Price',
  Tags: 'Tags',
  Category: 'Category',
  Make: 'Make?',
  Buy: 'Buy?',
  Sale: 'Sale?',
  ItemType: 'Item Type',
} as const;

export const CreateColumn = {
  ...CommonColumn,
} as const;

export const UpdateColumn = {
  UniqueID: "Unique ID (Can't change)",
  ProductVariantName: "Product Variant Name (Can't change)",
  ...CommonColumn,
} as const;

interface FieldConfigType {
  name: string;
  type: 'Text' | 'Dropdown';
  options: string[];
  required: boolean;
  allowCustomValue: boolean;
}

interface VariantDetail {
  id?: string;
  sku: string;
  purchasePrice: number;
  sellingPrice: number;
  customFields: { key: string; value: string }[];
  attributeFields: { key: string; value: string }[];
}

function getPayload(rows: Record<string, string>[] = []) {
  const productsData = rows.map((row) => {
    const metadataConfigs: FieldConfigType[] = [];
    const metadataFields: { key: string; value: string }[] = [];
    const variantConfigs: FieldConfigType[] = [];
    const variantFields: { key: string; value: string }[] = [];

    const product = {
      ...(row[UpdateColumn.UniqueID] && { variantId: row[UpdateColumn.UniqueID] }),
      name: row[CreateColumn.ProductName],
      variantSku: row[CreateColumn.ProductVariantSku],
      measurementUnit: row[CreateColumn.UnitOfMeasure],
      defaultSalePrice: Number(row[CreateColumn.DefaultSalesPrice]),
      defaultPurchasePrice: Number(row[CreateColumn.DefaultPurchasePrice]),
      productTags: row[CreateColumn.Tags],
      productCategory: row[CreateColumn.Category],
      productType: row[CreateColumn.ItemType].toUpperCase(),
      make: row[CreateColumn.Make].toLowerCase().includes('yes'),
      purchase: row[CreateColumn.Buy].toLowerCase().includes('yes'),
      sell: row[CreateColumn.Sale].toLowerCase().includes('yes'),
      metadataConfigs,
      metadataFields,
      variantConfigs,
      variantFields,
    };

    // Extract variant configuration and values
    const variantOptionRegex = /^Variant Option \d+$/;
    for (const [key, value] of Object.entries(row)) {
      if (variantOptionRegex.test(key) && value) {
        const variantKey = value;
        const variantValue = row[key.replace('Option', 'Value')];

        variantConfigs.push({
          name: variantKey,
          type: 'Dropdown',
          options: [],
          required: false,
          allowCustomValue: true,
        });

        if (variantValue) {
          variantFields.push({ key: variantKey, value: variantValue });
        }
      }
    }

    // Extract metadata configuration and values
    const metadataNameRegex = /^Meta Data Name \d+$/;
    for (const [key, value] of Object.entries(row)) {
      if (metadataNameRegex.test(key) && value) {
        const metadataKey = value;
        const metadataValue = row[key.replace('Name', 'Value')];

        metadataConfigs.push({
          name: metadataKey,
          type: 'Text',
          options: [],
          required: false,
          allowCustomValue: true,
        });

        if (metadataValue) {
          metadataFields.push({ key: metadataKey, value: metadataValue });
        }
      }
    }

    return product;
  });

  // Group variants by their parent product. For an update CSV the grouping key must be the
  // product id from the Unique ID column, never the product name -- the name is user editable
  // and several products may legitimately share one.
  const groupedProducts = productsData.reduce(
    (acc, product, index) => {
      let groupKey = product.name;

      if (product.variantId) {
        const parsed = parseProductUniqueId(product.variantId);
        if (!parsed) {
          throw new Error(
            `Invalid '${UpdateColumn.UniqueID}' value at row ${index + 2}: '${product.variantId}'. ` +
              `Copy the value exactly as exported and do not edit this column.`,
          );
        }
        groupKey = parsed.productId;
        product.variantId = parsed.variantId;
      }

      if (!acc[groupKey]) acc[groupKey] = [];
      acc[groupKey].push(product);

      return acc;
    },
    {} as Record<string, typeof productsData>,
  );

  // Construct final payload
  const finalPayload = Object.entries(groupedProducts).map(([_, productVariants]) => {
    const {
      variantSku,
      defaultPurchasePrice,
      defaultSalePrice,
      metadataConfigs,
      metadataFields,
      variantConfigs,
      variantFields,
      ...baseProduct
    } = productVariants[0];

    const customFieldConfig: FieldConfigType[] = [];
    const attributeConfig: FieldConfigType[] = [];
    const variantsDetails: VariantDetail[] = [];

    // Process each variant under the same product group
    productVariants.forEach((variant) => {
      const variantDetail: VariantDetail = {
        sku: variant.variantSku,
        ...(variant.variantId && { id: variant.variantId }),
        purchasePrice: variant.defaultPurchasePrice,
        sellingPrice: variant.defaultSalePrice,
        customFields: variant.metadataFields,
        attributeFields: variant.variantFields,
      };

      // Deduplicate and accumulate field configs
      for (const config of variant.metadataConfigs) {
        if (!customFieldConfig.some((c) => c.name === config.name)) {
          customFieldConfig.push(config);
        }
      }

      for (const config of variant.variantConfigs) {
        if (!attributeConfig.some((c) => c.name === config.name)) {
          attributeConfig.push(config);
        }
      }

      // Update attributeConfig with actual options from variant values
      for (const { key, value } of variant.variantFields) {
        const fieldConfig = attributeConfig.find((cfg) => cfg.name === key);
        if (fieldConfig && !fieldConfig.options.includes(value)) {
          fieldConfig.options.push(value);
        }
      }

      variantsDetails.push(variantDetail);
    });

    delete baseProduct.variantId;
    return {
      ...baseProduct,
      variantCustomFieldConfig: customFieldConfig,
      variantAttributeConfig: attributeConfig,
      variantsDetails,
    };
  });

  return finalPayload;
}

function getCsvTemplate(type: 'create' | 'update' = 'create') {
  const includeUpdate = type === 'update';

  const header: string[] = [
    ...(includeUpdate ? [UpdateColumn.UniqueID] : []),
    CreateColumn.ProductName,
    ...(includeUpdate ? [UpdateColumn.ProductVariantName] : []),
    CreateColumn.ProductVariantSku,
    CreateColumn.UnitOfMeasure,
    CreateColumn.DefaultSalesPrice,
    CreateColumn.DefaultPurchasePrice,
    CreateColumn.Tags,
    CreateColumn.Category,
    CreateColumn.Make,
    CreateColumn.Buy,
    CreateColumn.Sale,
    CreateColumn.ItemType,
    'Variant Option 1',
    'Variant Value 1',
    'Variant Option 2',
    'Variant Value 2',
    'Meta Data Name 1',
    'Meta Data Value 1',
    'Meta Data Name 2',
    'Meta Data Value 2',
  ];

  const data = [
    {
      uniqueId: '77846443<T-shirt>34464877',
      variantName: 'T-shirt / Red / M',
      name: 'T-shirt',
      sku: 'TS-RED-M',
      uom: 'pcs',
      salesPrice: '250',
      purchasePrice: '150',
      tags: 'tag1,tag2',
      category: 'cat1',
      make: 'Yes',
      buy: 'Yes',
      sale: 'Yes',
      itemType: 'Product',
      variant1: 'Color',
      value1: 'Red',
      variant2: 'Size',
      value2: 'M',
      meta1: 'Fabric',
      metaValue1: 'Cotton',
      meta2: 'Gender',
      metaValue2: 'Unisex',
    },
    {
      uniqueId: '8784463<T-shirt>34464878',
      variantName: 'T-shirt / Blue / M',
      name: 'T-shirt',
      sku: 'TS-BLUE-M',
      uom: 'pcs',
      salesPrice: '300',
      purchasePrice: '200',
      tags: 'tag1,tag2',
      category: 'cat1',
      make: 'Yes',
      buy: 'Yes',
      sale: 'Yes',
      itemType: 'Product',
      variant1: 'Color',
      value1: 'Blue',
      variant2: 'Size',
      value2: 'M',
      meta1: 'Fabric',
      metaValue1: 'Cotton',
      meta2: 'Gender',
      metaValue2: 'Unisex',
    },
    {
      uniqueId: '44384817<Pen>34484817',
      variantName: 'Pen',
      name: 'Pen',
      sku: 'ST-PEN-BLACK',
      uom: 'pcs',
      salesPrice: '15',
      purchasePrice: '10',
      tags: 'tag1,tag2',
      category: 'cat1',
      make: 'Yes',
      buy: 'Yes',
      sale: 'Yes',
      itemType: 'Product',
      variant1: '',
      value1: '',
      variant2: '',
      value2: '',
      meta1: 'Ink Color',
      metaValue1: 'Black',
      meta2: 'Type',
      metaValue2: 'Ballpoint',
    },
  ];

  const rows = [header];

  for (const item of data) {
    const row: string[] = [
      ...(includeUpdate ? [item.uniqueId] : []),
      item.name,
      ...(includeUpdate ? [item.variantName] : []),
      item.sku,
      item.uom,
      item.salesPrice,
      item.purchasePrice,
      JSON.stringify(item.tags),
      item.category,
      item.make,
      item.buy,
      item.sale,
      item.itemType,
      item.variant1,
      item.value1,
      item.variant2,
      item.value2,
      item.meta1,
      item.metaValue1,
      item.meta2,
      item.metaValue2,
    ];
    rows.push(row);
  }

  return rows;
}

const MAX_IMAGE_UPLOAD_LIMIT = 200;

export const ProductImport: React.FC<{ redirection?: boolean }> = ({ redirection = true }) => {
  const [csvData, setCsvData] = useState<Record<string, any>[]>([]);
  const optionalPurchasePriceEnabled = useFeatureFlag(optionalPurchasePriceFlag);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [initialColumns, setInitialColumns] = useState<string[]>([]);
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  const [isDropdownVisible, setIsDropdownVisible] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const inputFolder = useRef<HTMLInputElement | null>(null);

  const { list } = useNavigation();

  const handleCsvUpload = async (info: UploadChangeParam<UploadFile<any>>) => {
    try {
      if (!info.fileList.length) return;

      let rowNumber = 0;
      const file = info.fileList[0].originFileObj!;
      const data = await readCSV({
        file,
        transformFunction: (row: Record<string, any>) => {
          const transformedRow: Record<string, any> = {
            ...row,
            [CreateColumn.ProductVariantSku]: excelSafeImport(row[CreateColumn.ProductVariantSku]),
          };

          const columns = Object.keys(transformedRow).map((key) => key);

          let csvType: 'CREATE' | 'UPDATE' = 'CREATE';
          if (columns[0] === UpdateColumn.UniqueID) {
            csvType = 'UPDATE';
          }

          rowNumber += 1;
          if (rowNumber === 1) {
            // throw error on missing required columns
            [
              csvType === 'UPDATE' ? UpdateColumn.UniqueID : null,
              CreateColumn.ProductName,
              csvType === 'UPDATE' ? UpdateColumn.ProductVariantName : null,
              CreateColumn.ProductVariantSku,
              CreateColumn.UnitOfMeasure,
              CreateColumn.DefaultSalesPrice,
              CreateColumn.DefaultPurchasePrice,
              CreateColumn.Tags,
              CreateColumn.Category,
              CreateColumn.Make,
              CreateColumn.Buy,
              CreateColumn.Sale,
              CreateColumn.ItemType,
            ]
              .filter(Boolean)
              .forEach((requiredColumn, idx) => {
                const column = columns[idx];
                if (requiredColumn !== column) {
                  throw new Error(
                    `Expected '${requiredColumn}' column at column ${idx + 1} but got '${column}'`,
                  );
                }
              });
          }

          // required fields value validation
          if (csvType === 'UPDATE' && !transformedRow[UpdateColumn.UniqueID]) {
            throw new Error(
              `'${UpdateColumn.UniqueID}' column is a required field. Which is missing at row ${
                rowNumber + 1
              }`,
            );
          }

          if (csvType === 'UPDATE' && !transformedRow[UpdateColumn.ProductVariantName]) {
            throw new Error(
              `'${
                UpdateColumn.ProductVariantName
              }' column is a required field. Which is missing at row ${rowNumber + 1}`,
            );
          }

          if (!transformedRow[CreateColumn.ProductName]) {
            throw new Error(
              `'${CreateColumn.ProductName}' column is a required field. Which is missing at row ${
                rowNumber + 1
              }`,
            );
          }

          if (!transformedRow[CreateColumn.ProductVariantSku]) {
            throw new Error(
              `'${
                CreateColumn.ProductVariantSku
              }' column is a required field. Which is missing at row ${rowNumber + 1}`,
            );
          }

          // if (!transformedRow[CreateColumn.UnitOfMeasure]) {
          //   throw new Error(
          //     `'${
          //       CreateColumn.UnitOfMeasure
          //     }' column is a required field. Which is missing at row ${rowNumber + 1}`
          //   );
          // }

          if (!transformedRow[CreateColumn.Make]) {
            throw new Error(
              `'${CreateColumn.Make}' column is a required field. Which is missing at row ${
                rowNumber + 1
              }`,
            );
          }
          if (!['Yes', 'No'].includes(transformedRow[CreateColumn.Make])) {
            throw new Error(
              `'${CreateColumn.Make}' column only accepts Yes or No. Which is missing at row ${
                rowNumber + 1
              }`,
            );
          }

          if (!transformedRow[CreateColumn.Buy]) {
            throw new Error(
              `'${CreateColumn.Buy}' column is a required field. Which is missing at row ${
                rowNumber + 1
              }`,
            );
          }
          if (!['Yes', 'No'].includes(transformedRow[CreateColumn.Buy])) {
            throw new Error(
              `'${CreateColumn.Buy}' column only accepts Yes or No. Which is missing at row ${
                rowNumber + 1
              }`,
            );
          }

          if (!transformedRow[CreateColumn.Sale]) {
            throw new Error(
              `'${CreateColumn.Sale}' column is a required field. Which is missing at row ${
                rowNumber + 1
              }`,
            );
          }
          if (!['Yes', 'No'].includes(transformedRow[CreateColumn.Sale])) {
            throw new Error(
              `'${CreateColumn.Sale}' column only accepts Yes or No. Which is missing at row ${
                rowNumber + 1
              }`,
            );
          }

          if (!transformedRow[CreateColumn.ItemType]) {
            throw new Error(
              `'${CreateColumn.ItemType}' column is a required field. Which is missing at row ${
                rowNumber + 1
              }`,
            );
          }
          if (!['Product', 'Material'].includes(transformedRow[CreateColumn.ItemType])) {
            throw new Error(
              `'${
                CreateColumn.ItemType
              }' column only accepts Product or Material. Which is missing at row ${rowNumber + 1}`,
            );
          }

          // product name validation
          if (!NAME_REGEX.test(transformedRow[CreateColumn.ProductName])) {
            throw new Error(
              `Please provide valid data for column ${CreateColumn.ProductName} at row ${
                rowNumber + 1
              }. ${getInvalidCharacterErrorMessage('name')}`,
            );
          }

          // product variant sku validation
          if (!SKU_REGEX.test(transformedRow[CreateColumn.ProductVariantSku])) {
            throw new Error(
              `Please provide valid data for column ${CreateColumn.ProductVariantSku} at row ${
                rowNumber + 1
              }. ${getInvalidCharacterErrorMessage('sku')}`,
            );
          }

          // number type validation on column 'selling price' field
          if (isNaN(transformedRow[CreateColumn.DefaultSalesPrice] || undefined)) {
            throw new Error(
              `Please provide valid data for column ${CreateColumn.DefaultSalesPrice} at row ${
                rowNumber + 1
              }`,
            );
          }

          // number type validation on column 'purchase price' field; blank is 0 when the
          // tenant has product.optionalPurchasePrice on (Stitches S2)
          if (
            !isValidImportPurchasePrice(
              transformedRow[CreateColumn.DefaultPurchasePrice],
              optionalPurchasePriceEnabled,
            )
          ) {
            throw new Error(
              `Please provide valid data for column ${CreateColumn.DefaultPurchasePrice} at row ${
                rowNumber + 1
              }`,
            );
          }

          const data: any = {
            ...transformedRow,
            [CreateColumn.ProductName]: transformedRow[CreateColumn.ProductName].trim(),
            [CreateColumn.ProductVariantSku]: transformedRow[CreateColumn.ProductVariantSku].trim(),
            [CreateColumn.UnitOfMeasure]: transformedRow[CreateColumn.UnitOfMeasure].trim(),
            [CreateColumn.DefaultSalesPrice]: transformedRow[CreateColumn.DefaultSalesPrice].trim(),
            [CreateColumn.DefaultPurchasePrice]:
              transformedRow[CreateColumn.DefaultPurchasePrice].trim(),
            [CreateColumn.Tags]: transformedRow[CreateColumn.Tags].trim().split(','),
            [CreateColumn.Category]: transformedRow[CreateColumn.Category].trim(),
            [CreateColumn.ItemType]: transformedRow[CreateColumn.ItemType].trim(),
            [CreateColumn.Make]: transformedRow[CreateColumn.Make].trim(),
            [CreateColumn.Buy]: transformedRow[CreateColumn.Buy].trim(),
            [CreateColumn.Sale]: transformedRow[CreateColumn.Sale].trim(),

            // conditionally add field for update csv
            ...(csvType === 'UPDATE'
              ? { [UpdateColumn.UniqueID]: transformedRow[UpdateColumn.UniqueID].trim() }
              : {}),
            ...(csvType === 'UPDATE'
              ? {
                  [UpdateColumn.ProductVariantName]:
                    transformedRow[UpdateColumn.ProductVariantName].trim(),
                }
              : {}),
          };

          return data;
        },
      });

      const duplicateSku = findDuplicate(
        data.filter(
          (item) => item[CreateColumn.ProductVariantSku] === CreateColumn.ProductVariantSku,
        ),
        CreateColumn.ProductVariantSku,
      );
      if (duplicateSku.length) {
        const uniqueDuplicateSku = Array.from(new Set(duplicateSku));
        notification.error({
          message: 'Failed',
          description: `Found ${duplicateSku.length} duplicate values for '${
            CreateColumn.ProductVariantSku
          }' - ${uniqueDuplicateSku.join(', ')}`,
        });
        return;
      }

      const payload = getPayload(data);
      for (let product of payload) {
        // missing variant options value validation
        const { variantAttributeConfig, variantsDetails, name } = product;
        const attributeNames = variantAttributeConfig.map((item) => item.name);
        variantsDetails.forEach(({ attributeFields, sku }) => {
          const fieldsName = attributeFields.map((item) => item.key);
          if (!attributeNames.every((name) => fieldsName.includes(name))) {
            throw Error(
              `Missing Variant Options value for ${CreateColumn.ProductName}: ${name},  ${CreateColumn.ProductVariantSku}: ${sku}`,
            );
          }
        });
      }

      const columnNames = Object.keys(data[0] ?? {}).map((column) => column);

      setCsvData(data);
      setSelectedColumns(columnNames);
      setInitialColumns(columnNames);
    } catch (err: any) {
      notification.error({
        message: 'Failed',
        description: err.message,
      });
    }
  };

  const handleCsvRemove = (_file: UploadFile<any>) => {
    setCsvData([]);
    setInitialColumns([]);
    setSelectedColumns([]);
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const payload = getPayload(csvData);
      if (payload?.[0]?.variantsDetails?.[0]?.id) {
        await importProductsForUpdate(payload);
      } else {
        await importProductsForCreate(payload);
      }

      if (redirection) list('products');
      notification.success({
        message: 'Success',
        description: 'Successfully imported product variants',
      });
    } catch (err: any) {
      notification.error({
        message: 'Failed',
        description: err.message,
      });
    }
    setIsLoading(false);
  };

  const columns = selectedColumns.map((key) => ({
    title: key,
    key: key,
    dataIndex: key,
  }));

  const menu = (
    <Menu
      multiple
      selectable
      selectedKeys={selectedColumns}
      onClick={({ key }) => {
        if (selectedColumns.includes(key)) {
          // at least one column must be selected
          if (selectedColumns.length === 1) return;

          setSelectedColumns((prev) => prev.filter((item) => item !== key));
        } else {
          setSelectedColumns((prev) => {
            const newColumns = [...prev, key];
            const sortedColumns = initialColumns.filter((key) => newColumns.includes(key));
            return sortedColumns;
          });
        }
      }}
      style={{
        maxHeight: '300px',
        overflowY: 'scroll',
      }}
    >
      {[...initialColumns].map((key) => (
        <Menu.Item key={key}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '1.5rem',
            }}
          >
            <span>{key}</span>
            {selectedColumns.includes(key) && <Icons.CheckOutlined />}
          </div>
        </Menu.Item>
      ))}
    </Menu>
  );

  const tableHeader = (
    <Dropdown
      overlay={menu}
      trigger={['click']}
      visible={isDropdownVisible}
      onVisibleChange={() => setIsDropdownVisible((visible) => !visible)}
    >
      <Button onClick={(e) => e.stopPropagation()}>
        Columns <Icons.DownOutlined />
      </Button>
    </Dropdown>
  );

  const handleImageUploadChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    if (files.length > MAX_IMAGE_UPLOAD_LIMIT) {
      notification.error({
        message: 'Error',
        description: 'You can upload up to 200 files at a time.',
      });
      e.target.value = '';
      e.target.files = null;
      return;
    }

    for (const file of files) {
      const size = file.size || 0;
      const format = file.type || '';

      if (!isLessThanSize(size, MAX_UPLOAD_FILE_SIZE_MB)) {
        notification.error({
          message: 'Error',
          description: `Files larger than ${MAX_UPLOAD_FILE_SIZE_MB}MB cannot be uploaded`,
        });
        e.target.value = '';
        e.target.files = null;
        return;
      }

      if (!isJpgOrPngFile(format, file.name || '')) {
        notification.error({
          message: 'Error',
          description: 'Supports: image/png, image/jpg, image/heic, image/heif',
        });
        e.target.value = '';
        e.target.files = null;
        return;
      }
    }
  };

  const handleImageUpload = async () => {
    if (!inputFolder.current?.files || inputFolder.current.files.length === 0) {
      return;
    }
    notification.info({
      message: 'On Progress',
      description: 'Uploading...',
    });

    const files = inputFolder.current.files;
    const newFilesObject: Array<any> = [];
    for (const file of files) {
      const newFileObj = {
        lastModified: file.lastModified,
        lastModifiedDate: new Date(file.lastModified),
        name: file.name,
        size: file.size,
        type: file.type,
        percent: 0,
        originFileObj: file,
        uid: crypto.randomUUID(),
      };
      newFilesObject.push(newFileObj);
    }

    const filesWithBase64String: Array<any> = [];
    for (const file of newFilesObject) {
      if (file.originFileObj) {
        const base64String = await file2Base64(file);
        const { originFileObj, ...rest } = file;
        filesWithBase64String.push({ ...rest, base64String });
      } else {
        filesWithBase64String.push(file);
      }
    }

    try {
      await uploadProductMedia(filesWithBase64String);
      notification.success({
        message: 'Success',
        description: 'All images uploaded successfully',
      });
    } catch (e: any) {
      notification.error({
        message: 'Error',
        description: e.message,
      });
    } finally {
      if (inputFolder.current) {
        inputFolder.current.value = '';
        inputFolder.current.files = null;
      }
    }
  };

  const handleModalOk = () => {
    handleImageUpload();
    setIsModalOpen(false);
  };

  const handleProductsAndImagesSync = async () => {
    try {
      await mapProductsAndImages();
      notification.success({
        message: 'Success',
        description: 'All images and products synced successfully',
      });
    } catch (e: any) {
      notification.error({
        message: 'Error',
        description: e.message,
      });
    }
  };

  const imageUploadGuideLines = (
    <div>
      <p>Single Product: Use sp_product-sku</p>
      <p>Example: sp_p001</p>
      <p>Parent Product: Use pp_product-sku</p>
      <p>Example: pp_p001</p>
      <p>Variant: Use v_product-sku</p>
      <p>Example: v_p001</p>
    </div>
  );

  return (
    <List title="Import Products">
      <Modal
        visible={isModalOpen}
        title="Upload Images"
        bodyStyle={{ overflowY: 'scroll' }}
        onOk={handleModalOk}
        onCancel={() => {
          setIsModalOpen(false);
          if (inputFolder.current) {
            inputFolder.current.value = '';
            inputFolder.current.files = null;
          }
        }}
      >
        <div id="file" style={{ maxHeight: '400px', textAlign: 'center' }}>
          <input
            type="file"
            aria-label="File browser example"
            ref={(node) => {
              inputFolder.current = node;

              if (node) {
                ['webkitdirectory', 'directory', 'mozdirectory'].forEach((attr) => {
                  node.setAttribute(attr, '');
                });
              }
            }}
            onChange={handleImageUploadChange}
          />
        </div>
      </Modal>

      <Row
        className={styles.importCardsRow}
        gutter={[
          { xs: 10, sm: 20, md: 32, lg: 48 },
          { xs: 10, sm: 20, md: 28, lg: 36 },
        ]}
        wrap
        style={{ marginBottom: '2rem' }}
      >
        <Col xs={24} sm={11} lg={12}>
          <DownloadCsvTemplateCard
            rows={getCsvTemplate('create')}
            title="Download .csv template for import (Create)"
          />
        </Col>
        <Col xs={24} sm={11} lg={12}>
          <DownloadCsvTemplateCard
            rows={getCsvTemplate('update')}
            title="Download .csv template for import (Update)"
          />
        </Col>

        <Col xs={24} sm={11} lg={12}>
          <ImportCsvDataCard onFileUploadChange={handleCsvUpload} onFileRemove={handleCsvRemove} />
        </Col>

        <Col xs={24} sm={11} lg={12}>
          <Card className={styles.importActionCard}>
            <div className={styles.importActionCardBody}>
              <div onClick={() => setIsModalOpen(true)} className={styles.importActionItem}>
                <Popover
                  title="Image Naming Guidelines*"
                  content={imageUploadGuideLines}
                  placement="right"
                >
                  <span className={styles.importActionPopoverTrigger}>
                    <Button className={styles.importActionButton} icon={<Icons.UploadOutlined />}>
                      <Typography.Text className={styles.importActionButtonText}>
                        Upload Images
                      </Typography.Text>
                    </Button>
                  </span>
                </Popover>
              </div>

              <div className={styles.importActionItem}>
                <Button
                  className={styles.importActionButton}
                  icon={<Icons.SyncOutlined />}
                  onClick={() => {
                    notification.info({ message: 'On Progress', description: 'Syncing...' });
                    handleProductsAndImagesSync();
                  }}
                >
                  <Typography.Text className={styles.importActionButtonText}>
                    Sync Products & Images
                  </Typography.Text>
                </Button>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {columns.length > 0 && (
        <>
          <Divider />
          {/* CSV Data Table */}
          <Row wrap={false}>
            <Col flex="auto">
              <Table
                rowKey={(row, idx) => idx!}
                columns={columns}
                dataSource={csvData}
                title={() => tableHeader}
                scroll={{ x: true }}
              />
            </Col>
          </Row>

          {/* Submit Button */}
          <Row justify="center" align="middle">
            <Button
              style={{ width: '300px' }}
              loading={isLoading}
              disabled={!csvData.length}
              onClick={handleSubmit}
            >
              Save
            </Button>
          </Row>
        </>
      )}
    </List>
  );
};
